import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAnalyticsData } from '@/hooks/useAnalyticsData';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { Users, Eye, Clock, TrendingUp, Globe, FileText, ShoppingBag, Megaphone, MapPin } from 'lucide-react';
import { LeadsManager } from './LeadsManager';
import { format } from 'date-fns';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

const STATUS_LABELS: Record<string, string> = {
  en_attente: 'En attente',
  confirme: 'Confirmé',
  loue: 'Loué',
  annule: 'Annulé',
};

export const AnalyticsDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const { data: analytics, isLoading, error } = useAnalyticsData(selectedPeriod);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card><CardContent className="p-6">
          <p className="text-destructive">Erreur lors du chargement des données analytics</p>
        </CardContent></Card>
      </div>
    );
  }

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Dashboard Analytics</h1>
          <p className="text-sm text-muted-foreground">Données des {selectedPeriod} derniers jours • Production uniquement</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <Button key={d} variant={selectedPeriod === d ? 'default' : 'outline'} onClick={() => setSelectedPeriod(d)} size="sm">
              {d}j
            </Button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard icon={<Users className="h-4 w-4" />} label="Sessions" value={analytics?.totalSessions || 0} />
        <MetricCard icon={<Eye className="h-4 w-4" />} label="Pages vues" value={analytics?.totalPageViews || 0} />
        <MetricCard icon={<TrendingUp className="h-4 w-4" />} label="Taux rebond" value={`${(analytics?.bounceRate || 0).toFixed(1)}%`} />
        <MetricCard icon={<Clock className="h-4 w-4" />} label="Durée moy." value={formatDuration(analytics?.avgSessionDuration || 0)} />
        <MetricCard icon={<ShoppingBag className="h-4 w-4" />} label="Réservations" value={analytics?.totalReservations || 0} highlight />
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="traffic">Trafic & Sources</TabsTrigger>
          <TabsTrigger value="geo">Géographie</TabsTrigger>
          <TabsTrigger value="products">Produits</TabsTrigger>
          <TabsTrigger value="leads">Leads & Formulaires</TabsTrigger>
          <TabsTrigger value="google-ads">Google Ads</TabsTrigger>
          <TabsTrigger value="leads-manager">Gestion Leads</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Trafic quotidien</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics?.dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={v => format(new Date(v), 'dd/MM')} />
                    <YAxis />
                    <Tooltip labelFormatter={v => format(new Date(v), 'dd/MM/yyyy')} />
                    <Area type="monotone" dataKey="sessions" stroke="#6366f1" fill="#6366f1" fillOpacity={0.3} name="Sessions" />
                    <Area type="monotone" dataKey="pageViews" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} name="Pages vues" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Appareils</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={analytics?.deviceTypes} cx="50%" cy="50%" labelLine={false}
                      label={({ device, sessions }) => `${device}: ${sessions}`}
                      outerRadius={80} dataKey="sessions">
                      {analytics?.deviceTypes?.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TRAFFIC */}
        <TabsContent value="traffic" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Pages les plus visitées</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.topPages?.map((p, i) => (
                    <div key={i} className="flex justify-between items-center p-2 rounded hover:bg-muted/50">
                      <span className="font-medium text-sm truncate max-w-[200px]">{p.page}</span>
                      <Badge variant="secondary">{p.views}</Badge>
                    </div>
                  ))}
                  {!analytics?.topPages?.length && <p className="text-sm text-muted-foreground">Aucune donnée</p>}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Sources de trafic</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.topSources?.map((s, i) => (
                    <div key={i} className="flex justify-between items-center p-2 rounded hover:bg-muted/50">
                      <span className="font-medium text-sm">{s.source}</span>
                      <Badge variant="secondary">{s.sessions}</Badge>
                    </div>
                  ))}
                  {!analytics?.topSources?.length && <p className="text-sm text-muted-foreground">Aucune donnée</p>}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Navigateurs</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={analytics?.browsers}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="browser" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="sessions" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GEOGRAPHY */}
        <TabsContent value="geo" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Globe className="h-4 w-4" /> Pays</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.countries?.length ? (
                  <div className="space-y-2">
                    {analytics.countries.map((c, i) => (
                      <div key={i} className="flex justify-between items-center p-2 rounded hover:bg-muted/50">
                        <span className="font-medium">{c.country}</span>
                        <Badge variant="secondary">{c.sessions} sessions</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Les données géographiques ne sont pas encore disponibles.</p>
                    <p className="text-xs mt-1">Elles nécessitent un service de géolocalisation IP.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Villes</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.cities?.length ? (
                  <div className="space-y-2">
                    {analytics.cities.map((c, i) => (
                      <div key={i} className="flex justify-between items-center p-2 rounded hover:bg-muted/50">
                        <span className="font-medium">{c.city}</span>
                        <Badge variant="secondary">{c.sessions}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Les données de villes ne sont pas encore disponibles.</p>
                    <p className="text-xs mt-1">Elles nécessitent un service de géolocalisation IP.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* PRODUCTS */}
        <TabsContent value="products" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Réservations par produit</CardTitle></CardHeader>
              <CardContent>
                {analytics?.reservationsByProduct?.length ? (
                  <>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.reservationsByProduct} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="product" type="category" width={150} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#22c55e" radius={[0, 4, 4, 0]} name="Réservations" />
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground py-4">Aucune réservation sur cette période</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Statut des réservations</CardTitle></CardHeader>
              <CardContent>
                {analytics?.reservationsByStatus?.length ? (
                  <div className="space-y-3">
                    {analytics.reservationsByStatus.map((s, i) => (
                      <div key={i} className="flex justify-between items-center p-3 rounded border">
                        <span className="font-medium">{STATUS_LABELS[s.status] || s.status}</span>
                        <span className="text-2xl font-bold">{s.count}</span>
                      </div>
                    ))}
                    <div className="pt-2 border-t flex justify-between items-center">
                      <span className="font-bold">Total</span>
                      <span className="text-2xl font-bold">{analytics.totalReservations}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4">Aucune réservation</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* LEADS */}
        <TabsContent value="leads" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <MetricCard icon={<FileText className="h-4 w-4" />} label="Formulaires soumis" value={analytics?.formSubmissions || 0} />
            <MetricCard icon={<TrendingUp className="h-4 w-4" />} label="Taux de conversion"
              value={`${analytics?.totalSessions && analytics?.formSubmissions
                ? ((analytics.formSubmissions / analytics.totalSessions) * 100).toFixed(2)
                : '0'}%`} />
            <MetricCard icon={<ShoppingBag className="h-4 w-4" />} label="Réservations" value={analytics?.totalReservations || 0} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Types de formulaires</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.topFormTypes?.map((f, i) => (
                    <div key={i} className="flex justify-between items-center p-2 rounded hover:bg-muted/50">
                      <span className="font-medium">{f.form_type}</span>
                      <Badge variant="secondary">{f.submissions}</Badge>
                    </div>
                  ))}
                  {!analytics?.topFormTypes?.length && <p className="text-sm text-muted-foreground">Aucun formulaire</p>}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Sources des leads</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.leadSources?.map((s, i) => (
                    <div key={i} className="flex justify-between items-center p-2 rounded hover:bg-muted/50">
                      <span className="font-medium">{s.source}</span>
                      <Badge variant="secondary">{s.leads}</Badge>
                    </div>
                  ))}
                  {!analytics?.leadSources?.length && <p className="text-sm text-muted-foreground">Aucune donnée</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* GOOGLE ADS */}
        <TabsContent value="google-ads" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <MetricCard icon={<Megaphone className="h-4 w-4" />} label="Sessions Google Ads" value={analytics?.googleAdsData?.sessions || 0} />
            <MetricCard icon={<ShoppingBag className="h-4 w-4" />} label="Conversions" value={analytics?.googleAdsData?.conversions || 0} highlight />
            <MetricCard icon={<TrendingUp className="h-4 w-4" />} label="Taux de conversion"
              value={`${analytics?.googleAdsData?.sessions
                ? ((analytics.googleAdsData.conversions / analytics.googleAdsData.sessions) * 100).toFixed(1)
                : '0'}%`} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Campagnes Google Ads</CardTitle>
              <CardDescription>Trafic et conversions par campagne (basé sur les paramètres UTM)</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics?.googleAdsData?.campaigns?.length ? (
                <div className="space-y-2">
                  {analytics.googleAdsData.campaigns.map((c, i) => (
                    <div key={i} className="flex justify-between items-center p-3 rounded border">
                      <span className="font-medium">{c.campaign}</span>
                      <div className="flex gap-3">
                        <Badge variant="outline">{c.sessions} visites</Badge>
                        <Badge variant="secondary">{c.conversions} conv.</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Aucune donnée Google Ads détectée.</p>
                  <p className="text-xs mt-1">Assurez-vous que vos URLs Google Ads contiennent les paramètres UTM (utm_source=google, utm_medium=cpc).</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LEADS MANAGER */}
        <TabsContent value="leads-manager"><LeadsManager /></TabsContent>
      </Tabs>
    </div>
  );
};

// Reusable metric card
const MetricCard = ({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: string | number; highlight?: boolean }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{label}</CardTitle>
      <span className="text-muted-foreground">{icon}</span>
    </CardHeader>
    <CardContent>
      <div className={`text-2xl font-bold ${highlight ? 'text-primary' : ''}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </CardContent>
  </Card>
);
